/**
 * Lineage Page - Data lineage visualization and management
 * 
 * This page provides tools for discovering, viewing, and managing
 * data lineage relationships across the organization.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShareIcon,
  CubeIcon,
  UserGroupIcon,
  PlayIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import lineageApiService from '../../api/lineageApiService';
import { useConnection } from '../../contexts/EnhancedConnectionContext';
import { useUI } from '../../contexts/UIContext';
import { FeatureGate } from '../../components/billing';

const LineagePage = () => {
  const navigate = useNavigate();
  const { activeConnection } = useConnection();
  const { showNotification, updateBreadcrumbs } = useUI();
  const [lineageStatus, setLineageStatus] = useState(null);
  const [stakeholders, setStakeholders] = useState([]);
  const [discoveryRuns, setDiscoveryRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  // Single source of truth for query parameters
  const [queryState, setQueryState] = useState({
    filters: {
      objectType: 'all',
      businessCriticality: 'all',
      database: 'all',
      schema: 'all'
    },
    search: '',
    currentPage: 1,
    pageSize: 50
  });
  
  // Results from API
  const [results, setResults] = useState({
    objects: [],
    totalCount: 0,
    totalPages: 0
  });
  

  const loadLineageData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [statusResponse, stakeholdersResponse] = await Promise.all([
        lineageApiService.getLineageStatus(),
        lineageApiService.getStakeholderGroups()
      ]);

      if (statusResponse.success) {
        setLineageStatus(statusResponse.data);
      }
      
      if (stakeholdersResponse.success) {
        setStakeholders(stakeholdersResponse.data);
      }

      // Load discovery runs if we have an active connection
      if (activeConnection) {
        try {
          const runsResponse = await lineageApiService.getDiscoveryRuns(activeConnection.id);
          if (runsResponse.success) {
            setDiscoveryRuns(runsResponse.data);
          }
        } catch (runsError) {
          console.warn('Error loading discovery runs:', runsError);
          setDiscoveryRuns([]);
        }
      }

    } catch (error) {
      console.error('Error loading lineage data:', error);
    } finally {
      setLoading(false);
    }
  }, [activeConnection]);

  // Clean data loading function - loads data based on current queryState
  const loadDataObjects = useCallback(async (query = queryState) => {
    try {
      const offset = (query.currentPage - 1) * query.pageSize;
      const params = {
        limit: query.pageSize,
        offset: offset
      };
      
      // Add search parameter if provided
      if (query.search) {
        params.search = query.search;
      }
      
      // Add filter parameters if not 'all'
      if (query.filters.objectType !== 'all') {
        params.object_type = query.filters.objectType;
      }
      if (query.filters.businessCriticality !== 'all') {
        params.business_criticality = query.filters.businessCriticality;
      }
      if (query.filters.database !== 'all') {
        params.database = query.filters.database;
      }
      if (query.filters.schema !== 'all') {
        params.schema = query.filters.schema;
      }
      
      console.log('Loading data objects with params:', params);
      console.log('Query state:', query);
      
      const objectsResponse = await lineageApiService.getDataObjects(params);
      console.log('API Response:', objectsResponse);

      if (objectsResponse.success) {
        const objects = objectsResponse.data.objects || [];
        const totalCount = objectsResponse.data.total_count || 0;
        
        console.log('Setting results - objects:', objects.length, 'totalCount:', totalCount);
        console.log('First 3 object database names:', objects.slice(0, 3).map(o => o.database_name));
        
        // Update results state
        setResults({
          objects: objects,
          totalCount,
          totalPages: Math.ceil(totalCount / query.pageSize)
        });
      } else {
        console.error('API call failed:', objectsResponse);
        setResults({ objects: [], totalCount: 0, totalPages: 0 });
      }
    } catch (error) {
      console.error('Error loading data objects:', error);
      setResults({ objects: [], totalCount: 0, totalPages: 0 });
    }
  }, [queryState]);

  // Single effect to load data when queryState changes
  useEffect(() => {
    if (!loading) {
      loadDataObjects();
    }
  }, [queryState, loading, loadDataObjects]);

  // Load initial data when connection changes
  useEffect(() => {
    // Clear breadcrumbs to use path-based breadcrumbs for Lineage page
    updateBreadcrumbs([]);
    loadLineageData();
  }, [activeConnection, loadLineageData, updateBreadcrumbs]);

  const handleDiscoverLineage = async () => {
    if (!activeConnection) return;
    
    try {
      setDiscovering(true);
      const response = await lineageApiService.discoverLineage(activeConnection.id);
      
      if (response.success) {
        // Reload data after discovery
        await loadLineageData();
        showNotification(
          `Lineage discovery completed!\n\nDiscovered:\n- ${response.objects_discovered || 0} data objects\n- ${response.relationships_discovered || 0} relationships\n- ${response.stakeholders_identified || 0} stakeholder mappings`,
          'success'
        );
      } else {
        showNotification(`Discovery failed: ${response.error}`, 'error');
      }
    } catch (error) {
      console.error('Error discovering lineage:', error);
      showNotification(`Discovery failed: ${error.message}`, 'error');
    } finally {
      setDiscovering(false);
    }
  };

  const handleViewObject = (object) => {
    // URL shows human-readable full_name, but pass UUID via state for backend API calls
    navigate(`/lineage/table/${encodeURIComponent(object.full_name)}`, {
      state: { objectId: object.id }
    });
  };

  const getObjectTypeIcon = (objectType) => {
    switch (objectType) {
      case 'table': return <CubeIcon className="h-5 w-5 text-blue-500" />;
      case 'view': return <ShareIcon className="h-5 w-5 text-green-500" />;
      default: return <CubeIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getCriticalityBadge = (criticality) => {
    const config = {
      critical: { bg: 'bg-red-100', text: 'text-red-800', label: 'Critical' },
      high: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'High' },
      medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Medium' },
      low: { bg: 'bg-green-100', text: 'text-green-800', label: 'Low' },
      unknown: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Unknown' }
    };

    const style = config[criticality] || config.unknown;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        {style.label}
      </span>
    );
  };

  const getDiscoveryStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'running': return <ClockIcon className="h-5 w-5 text-yellow-500 animate-spin" />;
      case 'failed': return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      default: return <InformationCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  // Helper functions to update query state (automatically triggers reload)
  const updateFilter = (key, value) => {
    setQueryState(prev => ({
      ...prev,
      filters: { 
        ...prev.filters, 
        [key]: value,
        // Reset schema when database changes
        ...(key === 'database' ? { schema: 'all' } : {})
      },
      currentPage: 1 // Reset pagination when filtering
    }));
  };

  const updateSearch = (value) => {
    setQueryState(prev => ({
      ...prev,
      search: value,
      currentPage: 1 // Reset pagination when searching
    }));
  };

  const updatePagination = (updates) => {
    setQueryState(prev => ({
      ...prev,
      ...updates
    }));
  };

  // Get unique databases and schemas for filters from all data (not just current page)
  const [availableDatabases, setAvailableDatabases] = useState([]);
  const [availableSchemas, setAvailableSchemas] = useState([]);
  
  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        // Get all objects just for filter options (without pagination)
        const optionsResponse = await lineageApiService.getDataObjects({
          limit: 1000, // Get enough to show all filter options
          offset: 0
        });
        
        if (optionsResponse.success && optionsResponse.data.objects) {
          const allObjects = optionsResponse.data.objects;
          
          // Extract unique databases
          const databases = [...new Set(allObjects.map(obj => obj.database_name).filter(Boolean))].sort();
          console.log('Available databases from API:', databases);
          setAvailableDatabases(databases);
          
          // Extract schemas based on selected database
          const schemas = [...new Set(
            allObjects
              .filter(obj => queryState.filters.database === 'all' || obj.database_name === queryState.filters.database)
              .map(obj => obj.schema_name)
              .filter(Boolean)
          )].sort();
          setAvailableSchemas(schemas);
        }
      } catch (error) {
        console.error('Error loading filter options:', error);
      }
    };
    
    if (!loading) {
      loadFilterOptions();
    }
  }, [queryState.filters.database, loading]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading lineage data...</span>
      </div>
    );
  }

  // Lineage discovery is Snowflake-only today. For BigQuery connections,
  // `collector.collect_tier5_lineage` early-returns a no-op (the underlying
  // `services/lineage_discovery.py` is Snowflake-specific end-to-end), so
  // the page would otherwise render an empty state with no explanation.
  // Tracked in BUGS.md → B4 + BACKLOG.md → "BigQuery Tier 5 Lineage Support".
  const isBigQueryConnection = activeConnection?.connection_type === 'bigquery';

  return (
    <FeatureGate
      feature="lineage_enabled"
      upgradeMessage="Visualize data flow and understand upstream/downstream dependencies across your data warehouse."
    >
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Data Lineage</h1>
          <p className="text-gray-600 mt-1">
            Discover and visualize data relationships and business impact
          </p>
        </div>

        <div className="flex items-center gap-3">
          {!activeConnection ? (
            <div className="text-sm text-gray-500">
              No active connection. <a href="/connections" className="text-blue-600 hover:underline">Select a connection</a> from the header.
            </div>
          ) : (
            <div className="text-sm text-gray-600">
              Active connection: <span className="font-medium">{activeConnection.name}</span>
            </div>
          )}

          <button
            onClick={handleDiscoverLineage}
            disabled={discovering || !activeConnection || isBigQueryConnection}
            className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              !activeConnection
                ? 'Select a connection from the header first'
                : isBigQueryConnection
                ? 'Lineage discovery is not yet available for BigQuery — coming H2 2026'
                : discovering
                ? 'Discovery in progress'
                : 'Run lineage discovery'
            }
          >
            <PlayIcon className={`h-4 w-4 ${discovering ? 'animate-pulse' : ''}`} />
            {discovering ? 'Discovering...' : 'Discover Lineage'}
          </button>
        </div>
      </div>

      {/* BigQuery-not-supported banner. Clearer than letting the page render
          an empty state and leave the user wondering whether discovery
          silently failed. */}
      {isBigQueryConnection && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
          <div className="flex">
            <InformationCircleIcon className="h-5 w-5 text-amber-500 flex-shrink-0" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">
                Lineage not yet available for BigQuery
              </h3>
              <p className="mt-1 text-sm text-amber-700">
                Lineage discovery currently supports Snowflake only. BigQuery
                lineage is on the roadmap for H2 2026 and will use
                INFORMATION_SCHEMA.JOBS_BY_PROJECT to map real query flow.
                All other Sparvi features (validations, anomaly detection,
                schema change tracking, profiling) work normally on this
                connection.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Data Objects</p>
              <p className="text-3xl font-bold text-gray-900">{lineageStatus?.total_objects || 0}</p>
            </div>
            <CubeIcon className="h-8 w-8 text-gray-400" />
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Relationships</p>
              <p className="text-3xl font-bold text-blue-600">{lineageStatus?.total_relationships || 0}</p>
            </div>
            <ShareIcon className="h-8 w-8 text-blue-400" />
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Stakeholder Groups</p>
              <p className="text-3xl font-bold text-green-600">{lineageStatus?.total_stakeholder_groups || 0}</p>
            </div>
            <UserGroupIcon className="h-8 w-8 text-green-400" />
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Lineage Status</p>
              <p className={`text-sm font-bold ${lineageStatus?.lineage_enabled ? 'text-green-600' : 'text-gray-400'}`}>
                {lineageStatus?.lineage_enabled ? 'Active' : 'Not Enabled'}
              </p>
            </div>
            <div className={`h-3 w-3 rounded-full ${lineageStatus?.lineage_enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
          </div>
        </div>
      </div>

      {/* Discovery Runs */}
      {discoveryRuns.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Recent Discovery Runs</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {discoveryRuns.slice(0, 5).map((run) => (
                <div key={run.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {getDiscoveryStatusIcon(run.status)}
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {run.discovery_type} discovery
                      </p>
                      <p className="text-xs text-gray-500">
                        Started: {new Date(run.started_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right text-sm text-gray-600">
                    {run.status === 'completed' ? (
                      <div>
                        <p>{run.objects_discovered} objects, {run.relationships_discovered} relationships</p>
                        <p className="text-xs">{run.stakeholders_identified} stakeholders identified</p>
                      </div>
                    ) : (
                      <p className="capitalize">{run.status}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Data Objects */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Data Objects</h2>
            <div className="text-sm text-gray-600">
              {results.totalCount > 0 && (
                <>Showing {((queryState.currentPage - 1) * queryState.pageSize) + 1}-{Math.min(queryState.currentPage * queryState.pageSize, results.totalCount)} of {results.totalCount} objects</>
              )}
            </div>
          </div>
          
          {/* Search Input */}
          <div className="mb-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search data objects by name..."
                value={queryState.search}
                onChange={(e) => updateSearch(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          {/* Filters and Page Size Selector */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-wrap">
              <select
                value={queryState.filters.database}
                onChange={(e) => updateFilter('database', e.target.value)}
                className="text-sm border border-gray-300 rounded px-3 py-1"
              >
                <option value="all">All Databases</option>
                {availableDatabases.map(db => (
                  <option key={db} value={db}>{db}</option>
                ))}
              </select>
              
              <select
                value={queryState.filters.schema}
                onChange={(e) => updateFilter('schema', e.target.value)}
                className="text-sm border border-gray-300 rounded px-3 py-1"
              >
                <option value="all">All Schemas</option>
                {availableSchemas.map(schema => (
                  <option key={schema} value={schema}>{schema}</option>
                ))}
              </select>
              
              <select
                value={queryState.filters.objectType}
                onChange={(e) => updateFilter('objectType', e.target.value)}
                className="text-sm border border-gray-300 rounded px-3 py-1"
              >
                <option value="all">All Types</option>
                <option value="table">Tables</option>
                <option value="view">Views</option>
              </select>
              
              <select
                value={queryState.filters.businessCriticality}
                onChange={(e) => updateFilter('businessCriticality', e.target.value)}
                className="text-sm border border-gray-300 rounded px-3 py-1"
              >
                <option value="all">All Criticality</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            
            {/* Page Size Selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Show:</span>
              <select
                value={queryState.pageSize}
                onChange={(e) => {
                  const newPageSize = parseInt(e.target.value);
                  updatePagination({
                    pageSize: newPageSize,
                    currentPage: 1
                  });
                }}
                className="text-sm border border-gray-300 rounded px-2 py-1"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={results.totalCount > 0 ? results.totalCount : 500}>All</option>
              </select>
              <span className="text-sm text-gray-600">per page</span>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {results.objects.length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {results.objects.map((obj) => (
                <div key={obj.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    {getObjectTypeIcon(obj.object_type)}
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">{obj.full_name}</h3>
                      <p className="text-xs text-gray-500">
                        {obj.row_count?.toLocaleString() || 0} rows, {obj.column_count || 0} columns
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {getCriticalityBadge(obj.business_criticality)}
                    <button
                      onClick={() => handleViewObject(obj)}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      View Lineage
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CubeIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No data objects found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Run lineage discovery to find data objects and relationships.
              </p>
            </div>
          )}
          
          {/* Pagination Controls */}
          {results.totalCount > 0 && results.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <div className="flex items-center">
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {((queryState.currentPage - 1) * queryState.pageSize) + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(queryState.currentPage * queryState.pageSize, results.totalCount)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">{results.totalCount}</span>{' '}
                  results
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => updatePagination({ currentPage: Math.max(1, queryState.currentPage - 1) })}
                  disabled={queryState.currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  <span className="sr-only">Previous</span>
                </button>
                
                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, results.totalPages) }, (_, i) => {
                    let pageNumber;
                    if (results.totalPages <= 5) {
                      pageNumber = i + 1;
                    } else if (queryState.currentPage <= 3) {
                      pageNumber = i + 1;
                    } else if (queryState.currentPage >= results.totalPages - 2) {
                      pageNumber = results.totalPages - 4 + i;
                    } else {
                      pageNumber = queryState.currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => updatePagination({ currentPage: pageNumber })}
                        className={`relative inline-flex items-center px-3 py-2 text-sm font-medium border ${
                          pageNumber === queryState.currentPage
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => updatePagination({ currentPage: Math.min(results.totalPages, queryState.currentPage + 1) })}
                  disabled={queryState.currentPage === results.totalPages}
                  className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                  <span className="sr-only">Next</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stakeholder Groups */}
      {stakeholders.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Stakeholder Groups</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {stakeholders.map((group) => (
                <div key={group.id} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <UserGroupIcon className="h-5 w-5 text-gray-400" />
                    <h3 className="text-sm font-medium text-gray-900">{group.group_name}</h3>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">{group.description}</p>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {group.group_type}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
    </FeatureGate>
  );
};

export default LineagePage;